import { useParams } from "react-router-dom";
import { useGetPursuitTask } from "./useGetPursuitTask";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import TaskStatusSelect from "./TaskStatusSelect";
import TaskPrioritySelect from "./TaskPrioritySelect";
import DatePicker from "@/components/ui/date-picker";
import { useOptimisticUpdate } from "@/hooks/useOptimisticUpdate";
import { updatePursuitTask } from "@/features/pursuit/api/apiPursuitTask";
import { Ellipsis, Pen } from "lucide-react";
import AutoSaveInput from "@/ui/AutoSaveInput";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import TextEditor from "@/ui/TextEditor";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import PursuitTaskOperations from "./PursuitTaskOperations";
import { Button } from "@/components/ui/button";
import CardLoader from "@/ui/CardLoader";

export default function PursuitTask() {
  const { task_id, pursuit_id } = useParams();
  const { data: task, isLoading, isFetching } = useGetPursuitTask(task_id);
  const queryClient = useQueryClient();
  const { mutate: update } = useOptimisticUpdate(updatePursuitTask, [
    "pursuit-task",
    task_id,
  ]);

  useEffect(() => {
    return () => {
      queryClient.refetchQueries(["pursuit-task", task_id]);
      queryClient.invalidateQueries(["pursuit-tasks", pursuit_id]);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="p-4 max-w-5xl w-full justify-self-center">
        <CardLoader />
      </div>
    );
  }
  return (
    <div className="h-full bg-[var(--gray-50)]">
      <div className="p-4 max-w-5xl w-full justify-self-center">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center group gap-2">
              {isFetching ? (
                <div className="ml-3 pt-2 pb-1 border-none text-xl focus:ring-0 w-full ">
                  {task?.title}
                </div>
              ) : (
                <AutoSaveInput
                  initialValue={task?.title}
                  onSave={async (title) =>
                    await updatePursuitTask({ task_id, title })
                  }
                  placeholder="Task title"
                  className="!text-xl focus:ring-0 w-full hover:bg-muted"
                />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => e.stopPropagation()} // Prevents onClick from bubbling
                  >
                    <Ellipsis className="" />
                  </Button>
                </DropdownMenuTrigger>
                <PursuitTaskOperations
                  pursuitId={pursuit_id}
                  taskId={task.id}
                />
              </DropdownMenu>
            </CardTitle>
            <CardDescription>
              <div className="-ml-3 flex gap-2">
                <div className="flex items-center group">
                  <Pen className="group-hover:text-muted-foreground w-4 h-4 text-transparent transition duration-200 -mr-1.5" />
                  <TaskStatusSelect
                    defaultValue={task?.status}
                    onValueChange={(status) => update({ task_id, status })}
                  />
                </div>
                <div className="flex items-center group">
                  <Pen className="group-hover:text-muted-foreground w-4 h-4 text-transparent transition duration-200 -mr-1.5" />
                  <TaskPrioritySelect
                    defaultValue={task?.priority}
                    onValueChange={(priority) => update({ task_id, priority })}
                  />
                </div>
                <div className="flex items-center group">
                  <div>
                    <DatePicker
                      selected={task?.due_date && new Date(task.due_date)}
                      onSelect={(date) => update({ task_id, due_date: date })}
                      placeholder="Pick due date"
                      className="border-none bg-transparent w-full"
                    />
                  </div>
                </div>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-sm text-foreground/90 font-medium leading-none">
                Description
              </p>
              <TextEditor
                initialBlocks={task?.description?.blocks}
                editable={!isLoading}
                saveDataFn={async (content) => {
                  await updatePursuitTask({ task_id, description: content });
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
